import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import stackrCloudQr from "../../assets/stackr-cloud-qr.png";
import Logo from "../Logo";
import WeightDistributionBar from "../settings/vessel/WeightDistributionBar";
import SideWeightIndicator from "../settings/vessel/SideWeightDistribution";
import { getDepartureStabilityState } from "../utils/vesselState";
import {
  containerGroupedByAxis,
  getContainerTotalWeights,
  getHighestDraughtForWaterType,
  sideWeights,
} from "../utils/vesselWeightHelpers";

const WEIGHT_X_AXIS_TYPE = "x_axis";
const WEIGHT_Y_AXIS_TYPE = "y_axis";


const BLUE = "#064c7f";
const GREEN = "#147a3c";
const RED = "#bd2d2d";
const INK = "#18222c";
const BORDER = "#cdd4d9";

const formatDateLocal = (date: Date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatNumber = (value: number, digits = 0) =>
  Number.isFinite(value)
    ? new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(value)
    : "—";

const getEntityId = (value?: string) =>
  value?.split("/").filter(Boolean).pop() ?? "";

const Section = ({ number, title, children, sx = {} }) => (
  <Box
    sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: "4px",
      overflow: "hidden",
      backgroundColor: "#fff",
      ...sx,
    }}
  >
    <Typography
      component="h2"
      sx={{
        m: 0,
        px: 1.25,
        py: 0.75,
        borderBottom: `1px solid ${BORDER}`,
        color: BLUE,
        fontSize: "10.5px",
        fontWeight: 800,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
      }}
    >
      {number}. {title}
    </Typography>
    {children}
  </Box>
);

const DetailRow = ({ label, value, emphasis = false, valueColor = INK }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 2,
      py: 0.5,
    }}
  >
    <Typography sx={{ fontSize: "9.5px", color: INK }}>{label}</Typography>
    <Typography
      sx={{
        flexShrink: 0,
        fontSize: "9.5px",
        color: valueColor,
        fontWeight: emphasis ? 800 : 500,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const ImagePlaceholder = ({ children, width = 130, height = 54 }) => (
  <Box
    sx={{
      width,
      height,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: `1px dashed ${BORDER}`,
      color: "#71808c",
      backgroundColor: "#f7f9fa",
      textAlign: "center",
      fontSize: "8px",
      fontWeight: 800,
      letterSpacing: "0.08em",
      lineHeight: 1.35,
      textTransform: "uppercase",
    }}
  >
    {children}
  </Box>
);

const VesselLoadStabilityDocument = ({ vessel, containers, terminalCall, language }) => {
  const { t: localeT, i18n: i18nInstance } = useTranslation();
  const t = useMemo(
    () => (language && language !== i18nInstance.language ? i18nInstance.getFixedT(language) : localeT),
    [language, i18nInstance.language, localeT]
  );

  const containerGroupedByYAxis = containerGroupedByAxis(
    vessel,
    containers,
    terminalCall,
    WEIGHT_Y_AXIS_TYPE
  );
  const containerGroupedByXAxis = containerGroupedByAxis(
    vessel,
    containers,
    terminalCall,
    WEIGHT_X_AXIS_TYPE
  );

  if (
    !Array.isArray(containerGroupedByYAxis) ||
    !Array.isArray(containerGroupedByXAxis) ||
    containerGroupedByYAxis.length === 0 ||
    containerGroupedByXAxis.length === 0
  ) {
    return null;
  }

  const stabilityState = getDepartureStabilityState(vessel, terminalCall);
  const stability = stabilityState?.stability;
  const statistics = stabilityState?.statistics;
  const loaded = Number(stability?.cog_loaded?.z ?? 0);
  const maxLoaded = Number(stability?.cog_loaded_max?.z ?? 0);
  const hasMaximumKg = maxLoaded !== 1 && maxLoaded > 0;
  const isOverLoad = hasMaximumKg && loaded > maxLoaded;
  const tonnage = Number(statistics?.total_weight ?? 0);
  const draught = Number(statistics?.draught ?? 0);
  const maxDraught = getHighestDraughtForWaterType(
    "fresh",
    vessel?.totalWeightDraughtTable
  );
  const isDraughtOver = draught === 0 || Boolean(maxDraught && draught > maxDraught);
  const isTotalWeightOver =
    tonnage === 0 || Boolean(vessel?.maxWeight && tonnage > vessel.maxWeight);
  const isStable = !isOverLoad && !isDraughtOver && !isTotalWeightOver;
  const statusColor = isStable ? GREEN : RED;

  const vesselId = getEntityId(vessel?.["@id"]);
  const layerCount = vessel?.positions?.length ?? 0;
  const baseOffset = Number(vessel?.baseOffset ?? 0);
  const emptyKg = Number(vessel?.emptyCenterOfGravity?.z ?? 1);
  let cumulativeHeight = baseOffset;
  const layerData = Array.from({ length: layerCount + 1 }, (_, layer) => {
    if (layer === 0) {
      const weight = Number(vessel?.emptyWeightInTonnes ?? 1000);
      return { layer, weight, kg: emptyKg, moment: weight * emptyKg };
    }
    const layerContainers = containers.filter(
      (container) =>
        container.position?.[0] === layer &&
        String(container.position?.[3]) === vesselId
    );
    const weight = layerContainers.reduce(
      (total, container) => total + Number(container.weight ?? 0) / 1000,
      0
    );
    const heights = layerContainers.map(
      (c) => Number(c.containerType?.height ?? 2.89)
    );
    const avgHeight =
      heights.length > 0
        ? heights.reduce((a, b) => a + b, 0) / heights.length
        : 0;
    const kg = heights.length > 0 ? cumulativeHeight + avgHeight / 2 : 0;
    if (heights.length > 0) cumulativeHeight += avgHeight;
    return { layer, weight, kg, moment: weight * kg };
  });
  const totalLayerWeight = layerData.reduce((sum, row) => sum + row.weight, 0);
  const totalMoment = layerData.reduce((sum, row) => sum + row.moment, 0);
  const calculatedKg =
    totalLayerWeight > 0 ? totalMoment / totalLayerWeight : loaded;
  const shownKg = loaded || calculatedKg;
  const kgMargin = hasMaximumKg ? maxLoaded - shownKg : 0;
  const stabilityRatio =
    hasMaximumKg && maxLoaded > 0 ? (shownKg / maxLoaded) * 100 : 0;
  const plannedContainerWeight = containers.reduce(
    (sum, container) => sum + Number(container.weight ?? 0) / 1000,
    0
  );
  const totalContainers = containers.length;
  const totalTeu = Number(statistics?.total_teu ?? 0);

  const lengthTotals = { front: 0, middle: 0, back: 0 };
  if (
    Array.isArray(vessel?.weightGroup) &&
    vessel.weightGroup.length === containerGroupedByXAxis.length
  ) {
    vessel.weightGroup.forEach((group, index) => {
      if (group in lengthTotals) {
        lengthTotals[group] += Number(containerGroupedByXAxis[index] ?? 0);
      }
    });
  } else {
    getContainerTotalWeights(containerGroupedByXAxis).forEach(
      (group, index) => {
        const key = ["front", "middle", "back"][index];
        if (key) {
          lengthTotals[key] = Number(group.total ?? 0);
        }
      }
    );
  }

  const lengthValues = [
    { label: t("front", "Fwd"), value: lengthTotals.front },
    { label: t("middle", "Mid"), value: lengthTotals.middle },
    { label: t("back", "Aft"), value: lengthTotals.back },
  ];
  const totalLongitudinal =
    lengthValues.reduce((sum, item) => sum + item.value, 0) || 1;
  const beamWeights = [...containerGroupedByYAxis].reverse();
  const { port, starboard } = sideWeights(containerGroupedByYAxis);
  const printedAt = formatDateLocal(new Date());
  const reportReference =
    terminalCall?.reference ||
    terminalCall?.code ||
    getEntityId(terminalCall?.["@id"]) ||
    "STABILITY";
  const vesselName = vessel?.name || t("unnamedVessel", "Unnamed vessel");
  const vesselType = vessel?.type || vessel?.vesselType || t("inlandVessel", "Inland vessel");
  const voyageLabel =
    terminalCall?.voyage?.name ||
    terminalCall?.voyage?.reference ||
    terminalCall?.terminal?.name ||
    "—";

  return (
    <Box
      sx={{
        width: "760px",
        minHeight: "1060px",
        boxSizing: "border-box",
        p: "18px 20px 14px",
        backgroundColor: "#fff",
        color: INK,
        fontFamily: '"Arial", "Helvetica", sans-serif',
        fontSize: "10px",
        lineHeight: 1.45,
        "& *": { boxSizing: "border-box" },
      }}
    >
      <Box
        component="header"
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 205px",
          alignItems: "center",
          minHeight: "104px",
          pb: 1.5,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Box sx={{ pr: 2, textAlign: "left" }}>
          <Typography
            sx={{
              color: "#111",
              fontSize: "20px",
              fontWeight: 900,
              letterSpacing: "-0.02em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            {t("stabilityCalcReport", "Stability Calculation Report")}
          </Typography>
          <Typography
            sx={{
              mt: 0.35,
              fontSize: "11px",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {t("inlandContainerVessel", "Inland Container Vessel")}
          </Typography>
        </Box>

        <Box
          sx={{
            pl: 1.5,
            borderLeft: `1px solid ${BORDER}`,
            display: "grid",
            gridTemplateColumns: "82px 1fr",
            rowGap: 0.4,
          }}
        >
          {[
            { label: t("reportNo", "Report No."), value: reportReference },
            { label: t("dateAndTime", "Date & Time"), value: printedAt },
            { label: t("calculatedBy", "Calculated by"), value: t("stackrSystem", "Stackr System") },
            { label: t("voyagePlan", "Voyage / Plan"), value: voyageLabel },
          ].map(({ label, value }) => (
            <React.Fragment key={label}>
              <Typography sx={{ fontSize: "8.5px", fontWeight: 700 }}>
                {label}:
              </Typography>
              <Typography
                sx={{
                  fontSize: "8.5px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {value}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "205px 1fr 175px",
          minHeight: "122px",
          py: 1.5,
          alignItems: "center",
        }}
      >
        <Box sx={{ pr: 2, borderRight: `1px solid ${BORDER}` }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 900,
              textTransform: "uppercase",
            }}
          >
            {vesselName}
          </Typography>
          <Typography sx={{ mt: 0.45, fontSize: "8.5px" }}>
            <strong>EU ID:</strong>{" "}
            {vessel?.euid || vessel?.eni || "—"}
          </Typography>
          <Typography sx={{ mt: 0.3, fontSize: "8.5px" }}>
            <strong>MMSI:</strong>{" "}
            {vessel?.mmsi || "—"}
          </Typography>
          <Typography sx={{ mt: 0.3, fontSize: "8.5px" }}>
            <strong>{t("vesselTypeLabel", "Vessel Type")}:</strong> {vesselType}
          </Typography>
        </Box>

        <Box
          sx={{
            px: 2,
            display: "grid",
            gridTemplateColumns: "120px 1fr",
            rowGap: 1,
          }}
        >
          {[
            { label: t("referenceWaterLevel", "Reference Water Level"), value: `${t("freshWater", "Fresh water")}${maxDraught ? ` (${maxDraught} ${t("cmMax", "cm max.")})` : ""}` },
            { label: t("calculationMethod", "Calculation Method"), value: t("esTrinStandard", "ES-TRIN") },
            { label: t("softwareModule", "Software / Module"), value: t("stackrStabilityModule", "Stackr Stability Module") },
            { label: t("units", "Units"), value: t("metricUnits", "Metric (T, m, kg)") },
          ].map(({ label, value }) => (
            <React.Fragment key={label}>
              <Typography sx={{ color: BLUE, fontSize: "8.5px" }}>
                {label}:
              </Typography>
              <Typography sx={{ fontSize: "8.5px" }}>{value}</Typography>
            </React.Fragment>
          ))}
        </Box>


      </Box>

      <Box
        sx={{
          minHeight: "84px",
          mb: 1.5,
          px: 2,
          display: "grid",
          gridTemplateColumns: "195px 210px 1fr",
          alignItems: "center",
          border: `1px solid ${statusColor}`,
          borderRadius: "4px",
          backgroundColor: isStable ? "#f4faf6" : "#fff6f6",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 46,
              height: 52,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              backgroundColor: statusColor,
              clipPath:
                "polygon(50% 0, 95% 16%, 88% 72%, 50% 100%, 12% 72%, 5% 16%)",
              fontSize: "27px",
              fontWeight: 900,
            }}
          >
            {isStable ? "✓" : "!"}
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: "8px", textTransform: "uppercase" }}
            >
              {t("stabilityStatus", "Stability status")}:
            </Typography>
            <Typography
              sx={{
                color: statusColor,
                fontSize: "15px",
                fontWeight: 900,
                textTransform: "uppercase",
              }}
            >
              {isStable ? t("stable", "Stable") : t("attention", "Attention")}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            px: 2,
            borderLeft: `1px solid ${BORDER}`,
            borderRight: `1px solid ${BORDER}`,
          }}
        >
          {hasMaximumKg && (
            <DetailRow
              label={t("maxStabilityKG", "Max. stability KG")}
              value={formatNumber(maxLoaded, 3)}
              emphasis
              valueColor={statusColor}
            />
          )}
          <DetailRow
            label={t("stabilityKG", "Current stability KG")}
            value={formatNumber(shownKg, 5)}
            emphasis
            valueColor={statusColor}
          />
        </Box>

        <Typography sx={{ pl: 2, fontSize: "8.5px", lineHeight: 1.55 }}>
          {isStable
            ? t("stabilityMet", "The vessel meets the configured stability, draught and total-weight criteria.")
            : t("stabilityAttention", "One or more configured stability, draught or total-weight criteria require attention.")}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1.5,
        }}
      >
        <Section number="1" title={t("weightOverviewPerLayer", "Weight overview per layer")} sx={{ gridRow: "span 2" }}>
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              "& th, & td": {
                px: 1,
                py: 0.7,
                borderBottom: `1px solid ${BORDER}`,
                fontSize: "8.5px",
                textAlign: "right",
              },
              "& th:first-of-type, & td:first-of-type": { textAlign: "left" },
              "& th": { fontWeight: 800 },
              "& tbody tr:last-of-type td": {
                borderBottom: 0,
                fontWeight: 800,
                backgroundColor: "#f4f6f7",
              },
            }}
          >
            <thead>
              <tr>
                <th>{t("layer", "Layer")}</th>
                <th>{t("weightInTonnes", "Weight (T)")}</th>
                <th>KG</th>
                <th>{t("verticalMoment", "Vert. moment")}</th>
              </tr>
            </thead>
            <tbody>
              {[...layerData].reverse().map((row) => (
                <tr key={row.layer}>
                  <td>
                    {row.layer === 0
                      ? t("emptyVessel", "Lightship")
                      : row.layer}
                  </td>
                  <td>{formatNumber(row.weight, 2)}</td>
                  <td>{formatNumber(row.kg, 3)}</td>
                  <td>{formatNumber(row.moment, 2)}</td>
                </tr>
              ))}
              <tr>
                <td>{t("total", "Total")}</td>
                <td>{formatNumber(totalLayerWeight, 3)}</td>
                <td>{formatNumber(shownKg, 5)}</td>
                <td>{formatNumber(totalMoment, 3)}</td>
              </tr>
            </tbody>
          </Box>
          <Box sx={{ px: 1.25, py: 1 }}>
            <Typography sx={{ fontSize: "8.5px" }}>
              {t("kgExplanation", "KG = vertical centre of gravity above reference waterline")}
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: "8.5px" }}>
              {t("verticalMomentExplanation", "Vertical moment = weight × KG")}
            </Typography>
          </Box>
        </Section>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, gridRow: "span 2" }}>
        <Section number="2" title={t("longitudinalWeightDistribution", "Longitudinal weight distribution")} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", px: 1.25 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              textAlign: "center",
            }}
          >
            {lengthValues.map((item) => (
              <Box
                key={item.label}
                sx={{
                  py: 0.9,
                  borderRight: `1px solid ${BORDER}`,
                  "&:last-of-type": { borderRight: 0 },
                }}
              >
                <Typography sx={{ fontSize: "8.5px", fontWeight: 800 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: "11px" }}>
                  {formatNumber(item.value / 1000)} T
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ px: 0, py: 0.75 }}>
            <WeightDistributionBar
              containerWeights={containerGroupedByXAxis}
              weightGroup={vessel?.weightGroup || []}
            />
          </Box>
          </Box>
        </Section>

        <Section number="3" title={t("transverseWeightDistribution", "Transverse weight distribution")} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", px: 1, py: 1 }}>
            <Box sx={{ display: "flex", alignItems: "stretch" }}>
              <Box
                sx={{
                  width: 26,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  backgroundColor: RED,
                  fontSize: "8px",
                  fontWeight: 800,
                }}
              >
                {t("portInitial", "P")}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: `repeat(${Math.max(beamWeights.length, 1)}, 1fr)`,
                  borderTop: `1px solid ${BORDER}`,
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                {beamWeights.map((weight, index) => (
                  <Typography
                    key={index}
                    sx={{
                      py: 0.65,
                      borderRight:
                        index < beamWeights.length - 1
                          ? `1px solid ${BORDER}`
                          : 0,
                      textAlign: "center",
                      fontSize: "9px",
                    }}
                  >
                    {formatNumber(weight / 1000)} T
                  </Typography>
                ))}
              </Box>
              <Box
                sx={{
                  width: 26,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  backgroundColor: GREEN,
                  fontSize: "8px",
                  fontWeight: 800,
                }}
              >
                {t("starboardInitial", "SB")}
              </Box>
            </Box>
            <Box
              sx={{
                ml: "26px",
                mr: "26px",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                borderBottom: `1px solid ${BORDER}`,
              }}
            >
              <Typography
                sx={{
                  py: 0.45,
                  borderRight: `1px solid ${BORDER}`,
                  textAlign: "center",
                  fontSize: "9px",
                }}
              >
                {port} T
              </Typography>
              <Typography sx={{ py: 0.45, textAlign: "center", fontSize: "9px" }}>
                {starboard} T
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 1,
                "& .side-weight": { fontSize: "13px" },
              }}
            >
              <SideWeightIndicator
                vessel={vessel}
                containerWeights={containerGroupedByYAxis}
                side="port"
              />
              <SideWeightIndicator
                vessel={vessel}
                containerWeights={containerGroupedByYAxis}
                side="starboard"
              />
            </Box>
          </Box>
        </Section>

        <Section number="4" title={t("cargoSummary", "Cargo Summary")} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", px: 1.25 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: "9.5px", color: INK }}>
                {t("totalContainers", "Total containers")}
              </Typography>
              <Typography sx={{ fontSize: "9.5px", fontWeight: 800 }}>
                {totalContainers}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
              <Typography sx={{ fontSize: "9.5px", color: INK }}>
                TEU
              </Typography>
              <Typography sx={{ fontSize: "9.5px", fontWeight: 800 }}>
                {totalTeu}
              </Typography>
            </Box>
          </Box>
        </Section>
        </Box>

        <Section number="5" title={t("stabilityCriteria", "Stability criteria")} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ px: 1.25, py: 0.65, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <DetailRow
              label={t("maxAllowedKg", "Maximum allowed KG")}
              value={hasMaximumKg ? formatNumber(maxLoaded, 3) : t("notConfigured", "Not configured")}
            />
            <DetailRow
              label={t("calculatedKg", "Calculated KG")}
              value={formatNumber(shownKg, 5)}
            />
            <DetailRow
              label={t("marginKg", "Margin (max. KG - KG)")}
              value={hasMaximumKg ? formatNumber(kgMargin, 5) : "—"}
              emphasis
              valueColor={kgMargin >= 0 ? GREEN : RED}
            />
            <DetailRow
              label={t("stabilityRatio", "Stability ratio (KG / max. KG)")}
              value={hasMaximumKg ? `${formatNumber(stabilityRatio, 1)}%` : "—"}
              emphasis
              valueColor={isOverLoad ? RED : GREEN}
            />
          </Box>
        </Section>

        <Section number="6" title={t("draughtDisplacement", "Draught & displacement")} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ px: 1.25, py: 0.65, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <DetailRow
              label={t("totalPlannedContainerWeight", "Planned container weight")}
              value={`${formatNumber(plannedContainerWeight, 3)} T`}
            />
            <DetailRow
              label={t("totalDraught", "Total draught")}
              value={`${formatNumber(draught)} cm`}
              valueColor={isDraughtOver ? RED : INK}
            />
            <DetailRow
              label={t("totalWeightVessels", "Total vessel weight")}
              value={`${formatNumber(tonnage)} T`}
              valueColor={isTotalWeightOver ? RED : INK}
            />
          </Box>
        </Section>
      </Box>

      <Section number="7" title={t("summary", "Summary")} sx={{ mt: 1.5 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 260px",
            minHeight: "82px",
            p: 1.25,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, pr: 2.5, alignItems: "flex-start" }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                backgroundColor: GREEN,
                borderRadius: "2px",
                fontSize: "10px",
                fontWeight: 900,
                mt: 0.2,
              }}
            >
              ✓
            </Box>
            <Typography sx={{ fontSize: "8.5px", lineHeight: 1.65 }}>
              {t("complianceText", "The calculated loading condition complies with the applicable stability criteria based on the planned stowage. Any deviation between the planned and the actual loading condition, including container weights or positions, may affect the vessel's stability. The master is responsible for ensuring that the actual loading condition remains within the vessel's approved stability limits at all times.")}
            </Typography>
          </Box>
          <Box sx={{ pl: 2.5, borderLeft: `1px solid ${BORDER}` }}>
            <Typography sx={{ pb: 1.2, fontSize: "8.5px" }}>
              {t("approvedBy", "Approved / Checked by")}: ______________________
            </Typography>
            <Typography sx={{ pt: 1.2, fontSize: "8.5px" }}>
              {t("dateLabel", "Date")}: ____________________________________
            </Typography>
          </Box>
        </Box>
      </Section>

      <Box
        component="footer"
        sx={{
          mt: 1.5,
          display: "grid",
          gridTemplateColumns: "95px 1fr 190px",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Logo fillColor={BLUE} fontColor={BLUE} size="42px" fontSize="0.72rem" />
        <Box>
          <Typography
            sx={{
              mb: 0.4,
              fontSize: "8px",
              fontWeight: 800,
              textTransform: "uppercase",
            }}
          >
            {t("disclaimer", "Disclaimer")}
          </Typography>
          <Typography sx={{ fontSize: "7.2px", lineHeight: 1.45 }}>
            {t("disclaimerText", "This report is generated automatically by the Stackr Stability Module from the data provided. It is intended for internal planning purposes only. The captain remains responsible for the vessel's final loading condition and safety.")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Box
            component="img"
            src={stackrCloudQr}
            alt={t("qrAlt", "QR code for app.stackr.cloud")}
            sx={{ width: 66, height: 66, imageRendering: "pixelated" }}
          />
          <Box sx={{ pt: 0.4 }}>
            <Typography sx={{ fontSize: "8px", fontWeight: 800 }}>
              {t("stackrInlandSolutions", "Stackr Inland Solutions")}
            </Typography>
            <Typography sx={{ mt: 0.4, fontSize: "7px" }}>
              {t("softwareForInlandNav", "Software for Inland Navigation")}
            </Typography>
            <Typography sx={{ mt: 0.4, fontSize: "7px" }}>
              app.stackr.cloud
            </Typography>
          </Box>
        </Box>
      </Box>


    </Box>
  );
};

export default VesselLoadStabilityDocument;
